/**
 * The two scanners we point installers to, linked into the official EINSTAR
 * store through Awin (advertiser 105805, publisher 3123641, joined 2026-10-14).
 * The `url`s are the deep links Awin's link builder generated; `affiliateHref`
 * adds a `clickref` so Awin's reports say where the click came from.
 *
 * Nothing links to Awin directly. Every link, on the site or off it, is a short
 * link, ppfengine.com/go/<id>, which routes/go/[id] redirects to the deep link.
 * `?ref=` on it becomes the clickref: the site sends its placement, and a link
 * pasted somewhere else can carry its own (`/go/rockit?ref=youtube`).
 *
 * Same honesty rule as compat.ts: both are `tested`, because both are scanners
 * in our studio. The Rockit is the entry point, the Rigil the step up. Specs are
 * EINSTAR's own, from einstar.com. No prices: they change by region and
 * promotion, and content-gates allows no dollar figure but our own pricing.
 */
import type { Tier } from './compat';

export interface PartnerCode {
	code: string;
	/** Last valid day, YYYY-MM-DD. content-gates fails the build once it has passed. */
	until: string;
}

export interface ScannerPick {
	id: 'rockit' | 'rigil';
	/** The maker's own name for it, brand first. */
	brand: string;
	model: string;
	/** One line under the photo: why it is on the shelf. */
	role: string;
	tier: Extract<Tier, 'tested' | 'compatible'>;
	badge: string;
	blurb: string;
	specs: { k: string; v: string }[];
	image: { src: string; w: number; h: number; alt: string };
	url: string;
	code?: PartnerCode;
}

export const scannerPicks: ScannerPick[] = [
	{
		id: 'rockit',
		brand: 'EINSTAR',
		model: 'Rockit',
		role: 'In our studio: where to start',
		tier: 'tested',
		badge: 'Tested with PPF Engine',
		blurb:
			'The entry-level handheld we scan with. Its scans were flattened, cut and installed with PPF Engine, so we can vouch for it end to end.',
		specs: [
			{ k: 'Light', v: 'Blue laser and infrared' },
			{ k: 'Dark, glossy paint', v: 'Laser mode, no spray' },
			{ k: 'Cable-free', v: 'Built-in Wi-Fi, swappable battery' }
		],
		image: {
			src: '/img/einstar-rockit.webp',
			w: 477,
			h: 1000,
			alt: 'The EINSTAR Rockit handheld 3D scanner, seen from the front with its three cameras.'
		},
		url: 'https://www.awin1.com/cread.php?awinmid=105805&awinaffid=3123641&ued=https%3A%2F%2Fwww.einstar.com%2Fproducts%2Feinstar-rockit'
	},
	{
		id: 'rigil',
		brand: 'EinScan',
		model: 'Rigil',
		role: 'In our studio: the step up',
		tier: 'tested',
		badge: 'Tested with PPF Engine',
		blurb:
			'A laser handheld with its own screen and computer, designed for automotive aftermarket work. We scan with one in our studio too, and its scans were flattened, cut and installed with PPF Engine.',
		specs: [
			{ k: 'Light', v: 'Blue laser and infrared' },
			{ k: 'Resolution', v: 'Up to 0.05 mm' },
			{ k: 'Modes', v: 'Standalone, Wi-Fi 6 or wired' }
		],
		image: {
			src: '/img/einscan-rigil.webp',
			w: 704,
			h: 1000,
			alt: 'The EinScan Rigil handheld laser 3D scanner on its grip.'
		},
		url: 'https://www.awin1.com/cread.php?awinmid=105805&awinaffid=3123641&ued=https%3A%2F%2Fwww.einstar.com%2Fproducts%2Feinscan-rigil'
	}
];

export const rockit = scannerPicks[0];

/** Where on the site a scanner link sits. Sent to Awin as the clickref.
    `instagram` is /links, the page behind the Instagram bio link. */
export type AffiliatePlacement =
	'compat' | 'compat-checker' | 'scan-guide' | 'patterns-guide' | 'instagram';

/** The clickref for a short link that came with no usable `ref`. */
export const DEFAULT_CLICKREF = 'short-link';

/**
 * A `ref` from the query string, made safe to hand Awin: lowercase letters,
 * digits, `-` and `_`, at most 50 characters (Awin's clickref limit).
 */
export function clickref(ref: string | null): string {
	const clean = (ref ?? '')
		.toLowerCase()
		.replace(/[^a-z0-9_-]/g, '')
		.slice(0, 50);
	return clean || DEFAULT_CLICKREF;
}

export function findPick(id: string): ScannerPick | undefined {
	return scannerPicks.find((pick) => pick.id === id);
}

/** The short link, ppfengine.com/go/<id>. Every scanner link on the site uses this. */
export function shortHref(pick: ScannerPick, placement?: AffiliatePlacement): string {
	return placement ? `/go/${pick.id}?ref=${placement}` : `/go/${pick.id}`;
}

/** Where a short link lands: Awin's deep link, tagged with the clickref. */
export function affiliateHref(pick: ScannerPick, ref: string): string {
	return `${pick.url}&clickref=${encodeURIComponent(ref)}`;
}

export const affiliateNote =
	'Affiliate links. If you buy through them, EINSTAR pays us a commission and you pay nothing extra. Both are here because they are the scanners we use, not because of the link.';

export const affiliateNoteShort =
	'Affiliate links: EINSTAR pays us a commission if you buy, at no extra cost to you.';

/** Valid through the whole of its last day, wherever the buyer is. */
export function codeIsLive(code: PartnerCode, now: number): boolean {
	return now < Date.parse(`${code.until}T23:59:59-12:00`);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "21 Oct", the same on the server and in every browser. */
export function codeUntilLabel(code: PartnerCode): string {
	const [, m, d] = code.until.split('-').map(Number);
	return `${d} ${MONTHS[m - 1]}`;
}
